import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import { LineChart } from '@mui/x-charts/LineChart';
import { toNumber, formatInt } from '../../utils/format';
import WidgetShell from './WidgetShell';

function ScoreTrendWidget({ stats }) {
  const recentRunsForChart = React.useMemo(
    () =>
      Array.isArray(stats?.history?.recentRuns)
        ? stats.history.recentRuns.slice(0, 20).reverse()
        : [],
    [stats?.history?.recentRuns]
  );

  const chartScores = React.useMemo(
    () => recentRunsForChart.map((run) => toNumber(run?.finalScore)),
    [recentRunsForChart]
  );

  const chartAverageScore = React.useMemo(() => {
    if (!chartScores.length) return 0;
    return chartScores.reduce((acc, s) => acc + s, 0) / chartScores.length;
  }, [chartScores]);

  const chartAverageSeries = React.useMemo(
    () => chartScores.map(() => chartAverageScore),
    [chartScores, chartAverageScore]
  );

  const chartXAxisLabels = React.useMemo(
    () => chartScores.map((_, i) => `Run ${i + 1}`),
    [chartScores]
  );

  return (
    <WidgetShell
      icon={<ShowChartRoundedIcon color="primary" fontSize="small" />}
      title="Score Trend"
      subtitle="Last 20 runs"
      action={<Chip size="small" label={`${chartScores.length} runs`} />}
      cardSx={{
        background: (theme) =>
          `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(
            theme.palette.background.paper,
            0.97
          )} 55%)`,
      }}
    >
      {chartScores.length ? (
        <Stack spacing={1.2}>
          <Stack direction="row" spacing={2.5} alignItems="baseline">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Latest
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                {formatInt(chartScores[chartScores.length - 1])}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Average
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {formatInt(Math.round(chartAverageScore))}
              </Typography>
            </Box>
          </Stack>

          <LineChart
            height={220}
            margin={{ top: 12, right: 20, bottom: 30, left: 44 }}
            xAxis={[{ id: 'runIndex', data: chartXAxisLabels, scaleType: 'point' }]}
            series={[
              {
                id: 'runScore',
                label: 'Run Score',
                data: chartScores,
                color: '#2563eb',
                showMark: true,
                curve: 'linear',
              },
              {
                id: 'averageScore',
                label: 'Average',
                data: chartAverageSeries,
                color: '#f59e0b',
                showMark: false,
                curve: 'linear',
              },
            ]}
            grid={{ horizontal: true }}
            slotProps={{
              legend: {
                direction: 'row',
                position: { vertical: 'top', horizontal: 'right' },
              },
            }}
          />
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No run score data yet.
        </Typography>
      )}
    </WidgetShell>
  );
}

export default ScoreTrendWidget;
